<template>
  <w-card
    class="table-wrapper-card flex-grow-1 overflow-y-hidden"
    flat
    outlined
  >
    <slot
      name="table"
      :class="'w-table-module__table'"
      :headers="internalHeaders"
      :initial-headers="initialHeaders"
      :invoked-param-handler="invokedParamHandler"
      :with-default-footer="withDefaultFooter"
      :number-of-entries-colspan="numberOfEntriesColspan"
      :path="path"
      :get-data="getData"
      :is-clickable="isClickable"
      :show-select="showSelect"
      :selected="selected"
      :search="search"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :filters="filters"
      :mandatory-filters="mandatoryFilters"
      :truncate-data="truncateData"
      :truncate-options="truncateOptions"
      v-on="$listeners"
    >
      <LazyLoadingTable
        ref="lazyLoadingTable"
        class="w-table-module__table"
        :headers="internalHeaders"
        :hide-headers="hideHeaders"
        :initial-headers="initialHeaders"
        :invoked-param-handler="invokedParamHandler"
        :with-default-footer="withDefaultFooter"
        :number-of-entries-colspan="numberOfEntriesColspan"
        :path="path"
        :get-data="getData"
        :is-clickable="isClickable"
        :show-select="showSelect"
        :selected="selected"
        :search="search"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :mandatory-filters="mandatoryFilters"
        :filters="filters"
        :truncate-data="truncateData"
        :truncate-options="truncateOptions"
        :item-class="itemClass"
        :has-max-height="hasMaxHeight"
        is-base-level-table-module
        :group-by="groupBy"
        :show-group-by="showGroupBy"
        :disable-lazy-loading="disableLazyLoading"
        :dirty-dialog-is-open="dirtyDialogIsOpen"
        :load-total-hits="withTotalHits"
        enter-loading-state-initially
        :toggle-force-reload="toggleForceReload"
        @input="(value) => $emit('input', value)"
        v-on="$listeners"
      >
        <template #top="slotProps">
          <slot
            name="top"
            :slot-props="slotProps"
          >
            <WorkDataTableTop
              :show-number-of-items="withTotalHits"
              :number-of-items="slotProps.options.itemsPerPage"
              :further-informations="slotProps.options.furtherInformations"
            >
              <template #top-title>
                <slot name="top-title" />
              </template>
              <template #top-actions>
                <slot name="table-top-actions" />
              </template>
            </WorkDataTableTop>
          </slot>
        </template>

        <template
          v-for="(_, name) in dynamicScopedSlots"
          :slot="name"
          slot-scope="slotData"
        >
          <slot
            :name="name"
            v-bind="slotData"
          />
        </template>

        <template #group.header="{ group, headers, toggle, isOpen }">
          <td :colspan="headers.length">
            <w-btn
              :ref="group"
              class="group-row-icon"
              small
              icon
              :data-open="isOpen"
              @click="toggle"
            >
              <w-arrow-down :class="isOpen? 'flippedIcon' : ''">
                <template #tooltip>
                  {{ !isOpen ? $t('Common.unfold') : $t('Common.foldup') }}
                </template>
              </w-arrow-down>
            </w-btn>
            {{ group }}
          </td>
        </template>
      </LazyLoadingTable>
    </slot>
  </w-card>
</template>

<script>
import LazyLoadingTable from '@/components/base_components/tables/LazyLoadingTable';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'BaseLevelTableContainer',
  components: {
    WorkDataTableTop: () =>
      import('@/components/base_components/tables/components/WorkDataTableTop'),
    LazyLoadingTable,
  },
  props: {
    // data
    headers: { type: Array, required: true },
    hideHeaders: { type: Array, required: false },
    orderdHeaders: { type: Array, required: true },
    notVisibleHeaders: { type: Array, required: true },
    initialHeaders: { type: Array, required: true },
    selected: { type: Array, required: false },
    path: { type: String, required: true },
    search: { type: String, required: true },
    sortBy: { type: Array, required: true },
    sortDesc: { type: Array, required: true },
    filters: { type: Object, required: true },
    mandatoryFilters: { type: Object, required: false },
    numberOfEntriesColspan: { type: Number, required: false },
    truncateData: { type: Boolean, required: false },
    truncateOptions: { type: Object, required: true },
    // flags
    isClickable: { type: Boolean, required: true },
    showSelect: { type: Boolean, required: true },
    withDefaultFooter: { type: Boolean, required: true },
    hasMaxHeight: { type: Boolean, default: false },
    withTotalHits: { type: Boolean, default: true },
    toggleForceReload: { type: Boolean, default: false },
    // handler
    getData: { type: Function, required: false },
    invokedParamHandler: { type: Function, required: false },
    handleClickRow: { type: Function, required: false },
    itemClass: { type: [String, Function], required: false },
    groupBy: { type: String, required: false },
    showGroupBy: { type: Boolean, required: false },
    dirtyDialogIsOpen: { type: Boolean, default: false },
    disableLazyLoading: { type: Boolean, default: false }
  },
  computed: {
    internalHeaders() {
      const currentHeaderOrder = this.orderdHeaders;
      if (currentHeaderOrder === undefined) {
        return this.headers;
      }

      const clonedHeaders = cloneDeep(this.headers);

      const sortedHeaders = clonedHeaders.sort((header1, header2) => {
        const positionFilter1 = currentHeaderOrder.findIndex(
          (_value) => _value === header1.value
        );
        const positionFilter2 = currentHeaderOrder.findIndex(
          (_value) => _value === header2.value
        );

        if (positionFilter1 === positionFilter2)
          return 0;

        if (positionFilter1 === -1)
          return 1;

        if (positionFilter2 === -1)
          return -1;

        return positionFilter1 - positionFilter2;
      });

      if (this.notVisibleHeaders.length === 0) {
        return sortedHeaders;
      }

      return sortedHeaders.filter((_header) => {
        return this.notVisibleHeaders.includes(_header.value) === false;
      });
    },
    dynamicScopedSlots() {
      const clonedScopedSlots = cloneDeep(this.$scopedSlots);
      delete clonedScopedSlots['top']; // explicit defined in template
      return clonedScopedSlots;
    },
  },
  methods: {
    reloadTable() {
      this.$refs.lazyLoadingTable.reloadTable();
    },
  },
};
</script>


<style lang="scss" scoped>
.flippedIcon {
  -webkit-transform: scaleY(-1);
  transform: scaleY(-1);
}
.w-table-module__table ::v-deep .v-row-group__header {
  @include w-highlight-table;
  .group-row-icon .v-icon:not(.v-icon--disabled) > svg {
    color: var(--v-accent-color) !important;
  }
}
</style>

